/**
 * Zod schema for the Transfer Request form.
 *
 * Validates PRD transfer reason codes, required fields, and preference constraints.
 * The "informed client" checkbox is validated here but NOT persisted to FHIR.
 */

import { z } from 'zod';
import { TRANSFER_REASON_CODES } from '../lib/transferFhirConstants';
import type { TransferReasonCode } from '../lib/transferFhirConstants';

const transferReasonCodeSchema = z.enum(
  TRANSFER_REASON_CODES as unknown as [string, ...string[]],
) as z.ZodType<TransferReasonCode>;

export const transferFormSchema = z.object({
  transferReasons: z
    .array(transferReasonCodeSchema)
    .min(1, 'At least one transfer reason is required'),
  transferExplanation: z
    .string()
    .trim()
    .min(1, 'Transfer explanation is required'),
  recommendedClinicians: z.string().optional(),
  informedClient: z.literal(true, {
    message: 'You must confirm the client has been informed',
  }),
  sessionFormatPreference: z
    .enum(['in-person', 'virtual', 'hybrid', 'no-preference'])
    .optional(),
  availabilityPreference: z.array(z.string()).optional(),
  areasOfFocus: z
    .array(z.string())
    .max(3, 'Maximum 3 areas of focus')
    .optional(),
  providerPreferences: z.array(z.string()).optional(),
});

export type TransferFormInput = z.infer<typeof transferFormSchema>;
